/**
 * A Heroicon an editor picked in the CMS.
 *
 * Separate from `Icon` on purpose, and the difference is worth keeping straight:
 *
 * - `Icon` draws the **design system's own glyphs**, exported from Figma and referenced
 *   by name in code. Which ones exist is a design decision, there are five of them, and
 *   they are inlined because the header uses them on every page.
 * - This draws **an icon chosen as content**. Which ones exist is an editorial decision,
 *   the set is somebody else's (Heroicons, MIT), and the name arrives from the database.
 *
 * **The set is loaded on demand, and that is the whole reason this file has state.**
 * There are 324 icons: about 112 KB of path data, 30 KB gzipped, against a main bundle
 * of 103 KB. Inlining them made every page on the site carry the whole set so that one
 * section on one page could draw four of them. Importing the map inside the component
 * puts it in its own chunk, so a page with no icons pays nothing, and the page with the
 * mosaic fetches it once alongside the nine photographs it is already loading.
 *
 * The promise is cached at module scope, so a page with a dozen icons fetches once and
 * the ones that mount later render immediately.
 *
 * Because the name is content, it is validated rather than trusted: an icon that is not
 * in the set renders nothing instead of a broken glyph. That happens honestly - an icon
 * removed from `tools/heroicons/` while an entry still names it.
 */
import { useEffect, useState } from 'react';

type IconMap = Record<string, string>;

let cache: IconMap | null = null;
let pending: Promise<IconMap> | null = null;

function loadIcons(): Promise<IconMap> {
  if (!pending) {
    pending = import('./icons/heroicons.generated').then((m) => {
      cache = m.HEROICONS as unknown as IconMap;
      return cache;
    });
  }
  return pending;
}

export function HeroIcon({
  name, size = 24, className, strokeWidth = 1.5
}: { name?: string | null; size?: number; className?: string; strokeWidth?: number }) {
  const [icons, setIcons] = useState<IconMap | null>(cache);

  useEffect(() => {
    if (!name || cache) return;
    let alive = true;
    loadIcons().then((map) => { if (alive) setIcons(map); }).catch(() => { /* leaves the slot empty */ });
    return () => { alive = false; };
  }, [name]);

  const markup = name && icons ? icons[name] : undefined;
  if (!markup) {
    // The space is held while the set loads so the tile does not jump once it arrives.
    return name ? <span aria-hidden style={{ width: size, height: size }} className="inline-block shrink-0" /> : null;
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      width={size}
      height={size}
      aria-hidden
      focusable="false"
      className={`inline-block shrink-0 ${className ?? ''}`}
      // Build-time constant from a committed file: the name is content, the markup is not.
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
}
